import { navLinks } from '@/mocks/homeData';

interface ContactFooterProps {
  onOpenQuote: () => void;
}

export default function ContactFooter({ onOpenQuote }: ContactFooterProps) {
  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="relative bg-background-950 border-t border-background-800/50">
      {/* CTA Banner */}
      <div className="section-padding py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-primary-400 text-xs font-semibold tracking-[0.25em] uppercase mb-4">
            Ready to Source?
          </p>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-50 mb-6">
            Send Your Parts List
            <br />
            <span className="text-primary-400">We Will Source It</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-400 max-w-xl mx-auto mb-8 leading-relaxed">
            Have a part number or equipment model? Send your make and model — coverage extends beyond this list. We take it from there.
          </p>
          <button
            onClick={onOpenQuote}
            className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-background-950 text-sm font-bold rounded-md hover:bg-primary-400 transition-all duration-300 whitespace-nowrap tracking-wide uppercase"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-mail-send-line"></i>
            </span>
            Request a Quote
          </button>
        </div>
      </div>

      {/* Footer Content */}
      <div className="section-padding py-12 md:py-16 border-t border-background-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {/* Brand & Contact */}
            <div className="lg:col-span-1">
              <img
                src="https://storage.readdy-site.link/project_files/da605b4b-e525-4d78-92ac-a53f525d58ea/945392c7-1aaf-4a12-ae66-157c92416ec4_compressed_photo_2026-07-19_10-56-20.webp"
                alt="Avenor Meridian"
                className="h-10 w-auto object-contain mb-6"
              />
              <p className="text-sm text-foreground-400 leading-relaxed mb-6">
                Delivering trusted spare parts for mining, construction, marine, and power generation industries.
              </p>
              {/* Company Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm font-bold text-foreground-200 mb-2">AVENOR MERIDIAN L.L.C.</p>
                  <div className="flex items-start gap-3 text-sm text-foreground-300 leading-relaxed">
                    <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                      <i className="ri-map-pin-line text-primary-400 text-sm"></i>
                    </span>
                    <span>
                      Zalfa Building, Al Garhoud
                      <br />
                      165-0, Dubai, UAE
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-foreground-300">
                  <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                    <i className="ri-briefcase-line text-primary-400 text-sm"></i>
                  </span>
                  <span>
                    Trade License No. 1424424
                  </span>
                </div>
              </div>
              <div className="space-y-5">
                {/* Albert Mirro */}
                <div>
                  <p className="text-sm font-semibold text-foreground-200 mb-1">Albert Mirro</p>
                  <p className="text-xs text-foreground-500 mb-3">Regional Manager — Europe, Eurasia &amp; Middle East</p>
                  <div className="space-y-2.5">
                    <a
                      href="mailto:albert.mirro@avenormeridian.com"
                      className="flex items-center gap-3 text-sm text-foreground-300 hover:text-primary-400 transition-colors duration-300"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                        <i className="ri-mail-line text-primary-400 text-sm"></i>
                      </span>
                      albert.mirro@avenormeridian.com
                    </a>
                    <a
                      href="https://wa.me/381621851315"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm text-foreground-300 hover:text-primary-400 transition-colors duration-300"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                        <i className="ri-whatsapp-line text-primary-400 text-sm"></i>
                      </span>
                      +381 65 381 0056
                    </a>
                  </div>
                </div>

                {/* Alexander Garg */}
                <div>
                  <p className="text-sm font-semibold text-foreground-200 mb-1">Alexander Garg</p>
                  <p className="text-xs text-foreground-500 mb-3">Regional Manager — Middle East &amp; Europe</p>
                  <div className="space-y-2.5">
                    <a
                      href="mailto:alexander.garg@avenormeridian.com"
                      className="flex items-center gap-3 text-sm text-foreground-300 hover:text-primary-400 transition-colors duration-300"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                        <i className="ri-mail-line text-primary-400 text-sm"></i>
                      </span>
                      alexander.garg@avenormeridian.com
                    </a>
                    <a
                      href="https://wa.me/971585077382"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm text-foreground-300 hover:text-primary-400 transition-colors duration-300"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                        <i className="ri-whatsapp-line text-primary-400 text-sm"></i>
                      </span>
                      +971 58 507 7382
                    </a>
                  </div>
                </div>

                {/* Facebook */}
                <a
                  href="https://www.facebook.com/profile.php?id=61591718015505"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-foreground-300 hover:text-primary-400 transition-colors duration-300"
                >
                  <span className="w-8 h-8 flex items-center justify-center bg-background-900 border border-background-800/50 rounded-lg shrink-0">
                    <i className="ri-facebook-line text-primary-400 text-sm"></i>
                  </span>
                  Facebook
                </a>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-sm font-bold text-foreground-200 mb-5 tracking-wide uppercase">
                Navigation
              </h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(link.href);
                      }}
                      className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-300"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industries */}
            <div>
              <h4 className="text-sm font-bold text-foreground-200 mb-5 tracking-wide uppercase">
                Industries
              </h4>
              <ul className="space-y-3">
                {['Mining', 'Construction', 'Marine', 'Power Generation', 'Oil & Gas'].map((industry) => (
                  <li key={industry}>
                    <span className="text-sm text-foreground-400">{industry}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Equipment */}
            <div>
              <h4 className="text-sm font-bold text-foreground-200 mb-5 tracking-wide uppercase">
                Equipment
              </h4>
              <ul className="space-y-3">
                {['Compressors', 'Pumps', 'Turbines', 'Electrical', 'Automation', 'Instrumentation'].map((eq) => (
                  <li key={eq}>
                    <span className="text-sm text-foreground-400">{eq}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="section-padding py-6 border-t border-background-800/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-600">
            &copy; {new Date().getFullYear()} Avenor Meridian. Global Supply, Engineered Excellence. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-foreground-600 hover:text-foreground-400 transition-colors duration-300">
              Privacy Policy
            </a>
            <a href="#" className="text-xs text-foreground-600 hover:text-foreground-400 transition-colors duration-300">
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}